import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { authApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Lands here from an emailed magic-link: /login/verify?token=...&next=...
 * Verifies the token (single-step), stores the session, and redirects.
 */
const LoginVerify = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const ran = useRef(false);

  const token = searchParams.get('token');
  const next = searchParams.get('next') || '/';

  useEffect(() => {
    if (ran.current) return;       // tokens are single-use — never verify twice
    ran.current = true;

    (async () => {
      if (!token) { setError('This login link is missing its token.'); return; }
      try {
        const result = await authApi.verifyLoginLink(token);
        if (result.access_token) {
          login(result.access_token, result.user);
          if (result.user?.must_change_password) navigate('/change-password', { replace: true });
          else navigate(next, { replace: true });
        } else {
          setError(result.detail || 'This login link is invalid or has expired.');
        }
      } catch (err) {
        setError('Something went wrong verifying your link. Please request a new one.');
      }
    })();
  }, [token, next, login, navigate]);

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/logo.svg" alt="Macula Networks" className="portal-logo" />
          <h1>Client Portal</h1>
        </div>
        {error ? (
          <div className="login-form" style={{ textAlign: 'center' }}>
            <AlertCircle size={48} style={{ color: '#dc2626', margin: '8px auto 16px' }} />
            <div className="error-message">{error}</div>
            <button
              type="button"
              className="submit-button"
              style={{ marginTop: 16 }}
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <div className="login-form" style={{ textAlign: 'center', padding: '24px 0' }}>
            <Loader2 className="spinner" size={40} style={{ color: '#0ea5e9', margin: '0 auto 16px' }} />
            <p className="code-info">Signing you in…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginVerify;
